import { Fragment, type CSSProperties } from "react";
import type { OperatingCalendar } from "@opus-dt/shared-types";
import { cn } from "@/lib/utils";
import { useOrgTimezone } from "@/hooks/useOrgTimezone";
import { groupByLine, outOfWindowBandsPct } from "./production-timeline-geometry";
import { ExpedienteBands } from "./ExpedienteBands";
import { TimelineBlock } from "./TimelineBlock";
import { TL_GUTTER_PX, type TimelineRange } from "./timeline-range";
import type { ProductionOrder } from "../../types/production-orders.types.js";

// Track fluido: a área de dados é flex-1 (ocupa 100% da largura), colunas/blocos posicionados em %.
// CSS vars (px reais fora do CSS bundle): gutter da linha, piso de largura (scroll só
// quando o container é menor) e alturas do eixo/swimlane. Via classes `*-[var()]`.
const LANE_PX = 60;

interface Props {
  items: ProductionOrder[];
  range: TimelineRange;
  conflicts: Set<string>;
  nowPct: number | null;
  now: Date;
  onOpenOrder: (o: ProductionOrder) => void;
  // Calendário por linha (lineId → calendário|null); null/ausente ⇒ 24/7 (sem faixas).
  calendars?: Record<string, OperatingCalendar | null>;
}

// Grade gantt por linha (swimlane), ocupando 100% da largura. Eixo + posições em % parametrizados
// pela faixa (Dia/Semana/Mês); rola só quando o container é menor que o piso `--tl-min`.
export function TimelineGrid({
  items,
  range,
  conflicts,
  nowPct,
  now,
  onOpenOrder,
  calendars,
}: Props) {
  const lines = groupByLine(items);
  const timezone = useOrgTimezone();
  const vars = {
    "--tl-gutter": `${TL_GUTTER_PX}px`,
    "--tl-min": `${TL_GUTTER_PX + range.minDataWidthPx}px`,
    "--tl-axis": range.view === "day" ? "34px" : "44px",
    "--tl-lane": `${LANE_PX}px`,
  } as CSSProperties;
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <div style={vars} className="w-full min-w-[var(--tl-min)]">
        {/* eixo */}
        <div className="flex border-b border-border-default bg-bg-muted-rd">
          <div className="flex w-[var(--tl-gutter)] shrink-0 items-center border-r border-border-default px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-tertiary">
            Linha
          </div>
          <div className="relative h-[var(--tl-axis)] flex-1">
            {range.cols.map((c) => (
              <div
                key={c.key}
                style={{ "--l": `${c.offsetPct}%`, "--w": `${c.widthPct}%` } as CSSProperties}
                className="absolute bottom-0 left-[var(--l)] top-0 flex w-[var(--w)] flex-col items-center justify-center"
              >
                <span
                  className={cn(
                    "font-mono text-[10px] leading-none",
                    c.major ? "text-text-secondary" : "text-text-tertiary"
                  )}
                >
                  {c.label}
                </span>
                {c.sublabel ? (
                  <span className="mt-0.5 text-[9px] leading-none text-text-tertiary">
                    {c.sublabel}
                  </span>
                ) : null}
              </div>
            ))}
            {nowPct != null ? (
              <div
                style={{ "--x": `${nowPct}%` } as CSSProperties}
                className="pointer-events-none absolute bottom-0 left-[var(--x)] top-0 z-10 flex -translate-x-1/2 flex-col items-center"
              >
                <span className="rounded-sm bg-rd-danger-500 px-1.5 py-0.5 font-mono text-[9px] font-bold text-white">
                  AGORA
                </span>
                <span className="w-0.5 flex-1 bg-rd-danger-500" />
              </div>
            ) : null}
          </div>
        </div>

        {/* swimlanes */}
        {lines.length === 0 ? (
          <div className="p-8 text-center text-sm text-text-tertiary">
            Sem ordens neste período.
          </div>
        ) : (
          lines.map((g, i) => (
            <div
              key={g.lineId}
              className={cn("flex", i < lines.length - 1 && "border-b border-border-subtle")}
            >
              <div className="flex w-[var(--tl-gutter)] shrink-0 flex-col justify-center border-r border-border-default px-3.5">
                <div className="truncate text-sm font-medium">{g.name}</div>
                <div className="font-mono text-[10px] text-text-tertiary">
                  {g.orders.length} OP{g.orders.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="relative h-[var(--tl-lane)] flex-1" data-testid={`lane-${g.lineId}`}>
                <ExpedienteBands
                  bands={outOfWindowBandsPct(calendars?.[g.lineId], range, timezone)}
                />
                {range.cols.map((c) => (
                  <Fragment key={c.key}>
                    {c.weekend ? (
                      <div
                        style={
                          { "--l": `${c.offsetPct}%`, "--w": `${c.widthPct}%` } as CSSProperties
                        }
                        className="bg-bg-muted-rd/40 absolute bottom-0 left-[var(--l)] top-0 w-[var(--w)]"
                      />
                    ) : null}
                    <div
                      style={{ "--x": `${c.offsetPct}%` } as CSSProperties}
                      className={cn(
                        "absolute bottom-0 left-[var(--x)] top-0 w-px",
                        c.major ? "bg-border-subtle" : "bg-bg-muted-rd"
                      )}
                    />
                  </Fragment>
                ))}
                {nowPct != null ? (
                  <div
                    style={{ "--x": `${nowPct}%` } as CSSProperties}
                    className="absolute bottom-0 left-[var(--x)] top-0 z-[2] w-0.5 -translate-x-1/2 bg-rd-danger-500"
                  />
                ) : null}
                {g.orders.map((o) => (
                  <TimelineBlock
                    key={o.id}
                    o={o}
                    range={range}
                    conflict={conflicts.has(o.id)}
                    now={now}
                    onOpenOrder={onOpenOrder}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
